import React, { useState } from "react";
import { useIsAdmin } from "@/hooks/use-user-role";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuditLogs, useAuditLogDetail, useExportAuditLogs, type AuditLogFilters } from "@/hooks/use-audit-logs";
import { AuditLogDiffModal } from "@/components/admin/AuditLogDiffModal";
import { format, formatDistanceToNow } from "date-fns";
import { Download, Search, X, Calendar as CalendarIcon, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const FEATURE_FLAG_AUDIT_VIEWER = true; // Set via env or config

export default function AuditLogsPage() {
  const { isSiteAdmin, isLoading: roleLoading } = useIsAdmin();
  const { toast } = useToast();
  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    page_size: 50,
  });
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useAuditLogs(filters);
  const { data: selectedLog } = useAuditLogDetail(selectedLogId);
  const exportMutation = useExportAuditLogs();

  // Access control
  if (roleLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isSiteAdmin || !FEATURE_FLAG_AUDIT_VIEWER) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">
            Access denied. Site Administrator privileges required.
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleFilterChange = (key: keyof AuditLogFilters, value: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to page 1 when filters change
    }));
  };

  const handleReset = () => {
    setFilters({ page: 1, page_size: 50 });
  };

  const handleRefresh = () => {
    refetch();
    toast({
      title: "Refreshed",
      description: "Audit logs have been refreshed",
    });
  };

  const handleExport = async () => {
    try {
      await exportMutation.mutateAsync(filters);
      toast({
        title: "Export successful",
        description: "Audit logs have been downloaded as CSV",
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: "Failed to export audit logs",
        variant: "destructive",
      });
    }
  };

  const truncateChanges = (changes: any): string => {
    if (!changes) return "—";
    
    try {
      // Parse if string
      const parsed = typeof changes === "string" ? JSON.parse(changes) : changes;
      
      // Handle arrays (diff format)
      if (Array.isArray(parsed)) {
        const fields = parsed.slice(0, 3).map((item: any) => item.field || item).filter(Boolean);
        return fields.length > 0 
          ? fields.join(", ") + (parsed.length > 3 ? "..." : "")
          : "View details";
      }
      
      // Handle objects - show meaningful values
      const entries = Object.entries(parsed).filter(([k]) => 
        !['timestamp', 'user_agent', 'access_timestamp'].includes(k)
      );
      
      if (entries.length === 0) return "View details";
      
      const summary = entries.slice(0, 2).map(([key, val]) => {
        const displayKey = key.replace(/_/g, ' ');
        const displayVal = typeof val === 'string' && val.length > 20 
          ? val.substring(0, 20) + "..." 
          : String(val);
        return `${displayKey}: ${displayVal}`;
      });
      
      return summary.join("; ") + (entries.length > 2 ? "..." : "");
    } catch {
      return "View details";
    }
  };

  const getRiskBadgeVariant = (risk?: string): "default" | "secondary" | "destructive" => {
    if (!risk) return "default";
    switch (risk.toLowerCase()) {
      case "low":
        return "secondary";
      case "medium":
        return "default";
      case "high":
      case "critical":
        return "destructive";
      default:
        return "default";
    }
  };

  const formatDateTime = (dateStr: string | null | undefined): { relative: string; full: string } => {
    if (!dateStr) return { relative: "Unknown", full: "Invalid date" };
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return { relative: "Unknown", full: "Invalid date" };
    return {
      relative: formatDistanceToNow(date, { addSuffix: true }),
      full: format(date, "PPpp"),
    };
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Audit Logs</CardTitle>
          <CardDescription>
            View all system audit events (read-only). Site Admin access only.
            {!filters.search && !filters.action && !filters.entity_type && !filters.actor && !filters.from && !filters.to && (
              <span className="block mt-1 text-xs text-muted-foreground">
                Showing last 10 events. Use filters to search further.
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={filters.search || ""}
                onChange={(e) => handleFilterChange("search", e.target.value || undefined)}
                className="pl-9"
              />
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "justify-start text-left font-normal",
                    !filters.from && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.from ? format(new Date(filters.from), "PPP") : "From date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.from ? new Date(filters.from) : undefined}
                  onSelect={(date) => handleFilterChange("from", date?.toISOString())}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "justify-start text-left font-normal",
                    !filters.to && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.to ? format(new Date(filters.to), "PPP") : "To date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.to ? new Date(filters.to) : undefined}
                  onSelect={(date) => handleFilterChange("to", date?.toISOString())}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>

            <Select
              value={filters.action || "all"}
              onValueChange={(v) => handleFilterChange("action", v === "all" ? undefined : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="login_success">Login Success</SelectItem>
                <SelectItem value="login_failed">Login Failed</SelectItem>
                <SelectItem value="logout">Logout</SelectItem>
                <SelectItem value="password_reset_requested">Password Reset</SelectItem>
                <SelectItem value="admin_action_performed">Admin Action</SelectItem>
                <SelectItem value="sensitive_data_accessed">Sensitive Data Access</SelectItem>
                <SelectItem value="application_error">Application Error</SelectItem>
                <SelectItem value="security_hardening_applied">Security Update</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.entity_type || "all"}
              onValueChange={(v) => handleFilterChange("entity_type", v === "all" ? undefined : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Entity Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="system">System</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="facility_contact">Facility Contact</SelectItem>
                <SelectItem value="database">Database</SelectItem>
              </SelectContent>
            </Select>

            <Input
              placeholder="Actor (email/id)"
              value={filters.actor || ""}
              onChange={(e) => handleFilterChange("actor", e.target.value || undefined)}
            />
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleReset}>
              <X className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw className={cn("h-4 w-4 mr-2", isLoading && "animate-spin")} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exportMutation.isPending}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>

          {/* Table */}
          {error && (
            <div className="text-destructive text-sm">
              Error loading audit logs: {error.message}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading audit logs...</div>
          ) : data && data.items.length > 0 ? (
            <>
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Actor</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Risk</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead className="hidden md:table-cell">Organization</TableHead>
                      <TableHead className="hidden lg:table-cell">Changes</TableHead>
                      <TableHead className="hidden xl:table-cell">IP / Client</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((log) => {
                      const dateTime = formatDateTime(log.occurred_at);
                      return (
                        <TableRow
                          key={log.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => setSelectedLogId(log.id)}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") setSelectedLogId(log.id);
                          }}
                        >
                          <TableCell className="whitespace-nowrap" title={dateTime.full}>
                            {dateTime.relative}
                          </TableCell>
                        <TableCell className="font-mono text-xs">{log.actor_email}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.action}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getRiskBadgeVariant(log.risk_level)}>
                            {log.risk_level || "low"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                            <div className="space-y-0.5">
                              <div className="font-medium">{log.entity_name || "Unknown"}</div>
                              <div className="text-xs text-muted-foreground">
                                {log.entity_type || "N/A"} · {log.entity_id?.slice(0, 8) || "N/A"}
                              </div>
                            </div>
                          </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="text-sm truncate max-w-[150px]">
                            {log.organization_name || (log.organization_id ? <span className="text-muted-foreground font-mono text-xs">{log.organization_id}</span> : '-')}
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">
                          {truncateChanges(log.changes_json)}
                        </TableCell>
                        <TableCell className="hidden xl:table-cell text-xs">
                          <div>{log.ip || "-"}</div>
                          <div className="text-muted-foreground capitalize">{log.client}</div>
                        </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {(filters.page! - 1) * filters.page_size! + 1} to{" "}
                  {Math.min(filters.page! * filters.page_size!, data.total)} of {data.total} entries
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFilters((prev) => ({ ...prev, page: prev.page! - 1 }))}
                    disabled={filters.page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFilters((prev) => ({ ...prev, page: prev.page! + 1 }))}
                    disabled={filters.page! * filters.page_size! >= data.total}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No audit events match your filters.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Diff Modal */}
      <AuditLogDiffModal
        log={selectedLog || null}
        isOpen={!!selectedLogId}
        onClose={() => setSelectedLogId(null)}
      />
    </div>
  );
}
